import { Capacitor } from '@capacitor/core'
import { reactive, watch } from 'vue'
import { ElNotification } from 'element-plus'
import type { Router } from 'vue-router'
import api from '@/axios'
import { PushService } from './core'
import { pushPlatform, safePushRoute, type PushPermission } from './types'

export const pushState = reactive({ permission: 'prompt' as PushPermission, registered: false, error: false })
let service: Promise<PushService> | undefined
let stop: (() => void) | undefined
let currentUser: { auth: { id: number } | null } | undefined

export function initializePushNotifications(router: Router, user: { auth: { id: number } | null }): void {
  if (service) return
  currentUser = user
  const platform = pushPlatform(Capacitor.isNativePlatform(), Capacitor.getPlatform())
  const navigate = (value: unknown) => {
    const route = safePushRoute(value)
    if (route) void router.isReady().then(() => router.push(route))
  }
  service = (async () => {
    const adapter = platform === 'web' ? (await import('./web')).webPushAdapter()
      : (await import('./native')).nativePushAdapter(platform)
    const push = new PushService({
      adapter, storage: localStorage,
      post: (path, body, bearer) => api.post(path, body, { headers: { Authorization: `Bearer ${bearer}` } }),
      changed: () => Object.assign(pushState, { permission: push.permission, registered: push.registered, error: push.error }),
      events: {
        tap: data => navigate(data.route || '/profile'),
        received: message => {
          // Native foreground presentation belongs to the Capacitor plugin.
          if (platform === 'web') ElNotification({
            title: message.title || 'Donna Bella', message: message.body || '',
            onClick: () => navigate(message.data?.route || '/profile'),
          })
        },
      },
    })
    await push.initialize()
    return push
  })()
  stop = watch(() => user.auth, auth => {
    void service!.then(push => push.authenticate(auth?.id ?? null, localStorage.getItem('token'))).catch(() => { pushState.error = true })
  }, { immediate: true })
  let lastRefresh = 0
  const refresh = () => {
    if (document.visibilityState !== 'visible' || Date.now() - lastRefresh < 60000) return
    lastRefresh = Date.now()
    void service!.then(push => push.refresh()).catch(() => { pushState.error = true })
  }
  const fromWorker = (event: MessageEvent) => {
    if (event.data?.type === 'push:navigate') navigate(event.data.route)
  }
  window.addEventListener('online', refresh)
  document.addEventListener('visibilitychange', refresh)
  navigator.serviceWorker?.addEventListener('message', fromWorker)
  if (import.meta.hot) import.meta.hot.dispose(() => {
    stop?.()
    window.removeEventListener('online', refresh)
    document.removeEventListener('visibilitychange', refresh)
    navigator.serviceWorker?.removeEventListener('message', fromWorker)
    void service?.then(push => push.dispose())
  })
}

export async function registerPushNotifications(): Promise<PushPermission> {
  if (!service) return 'unsupported'
  const push = await service
  await push.authenticate(currentUser?.auth?.id ?? null, localStorage.getItem('token'))
  return push.register()
}

export async function unregisterPushNotifications(disable = true): Promise<void> {
  if (service) await (await service).unregister(disable)
}

export async function getPushPermissionStatus(): Promise<PushPermission> {
  return service ? (await service).status() : 'unsupported'
}
